import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import axios from "../../../../config/axios";
import Card from "../../../../components/Cards/Basic";
import {
  showErrorToast,
  showSuccessToast,
  showValidationErrorToast,
} from "src/components/Toasts";
import {
  faFolderPlus,
  faPencil,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../../../components/IconButton";
import CategoryIcon from "../../../../components/CategoryIcon";
import EditParkingSlotDialog from "./EditParkingSlotDialog";
import useModal from "../../../../components/Modal/hooks/useModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmDialog from "../../../../components/Modal/ConfirmDialog";
import EditParkingCategoryDialog from "./EditParkingCategoryDialog";
import ParkingSlotCategory from "./ParkingSlotCategory";
import { useDebounce } from "use-debounce";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import SortableRow from "./SortableRow";
import SelectBox from "../../../../components/Forms/SelectBox";
import Checkbox from "../../../../components/Forms/Checkbox";
import styles from "./ParkingSlotsPage.module.scss";

const ParkingSlotsPage = () => {
  const [parkingSlots, setParkingSlots] = useState(null);
  const [parkingSlotCategories, setParkingSlotCategories] = useState(null);
  const [parkingSlotsByCategory, setParkingSlotsByCategory] = useState({});
  const [parkingSlotCategoryTemplate, setParkingSlotCategoryTemplate] =
    useState({ settings: {} });

  const { isShow: showEdit, toggle: toggleEdit } = useModal();
  const { isShow: showEditCategory, toggle: toggleEditCategory } = useModal();
  const [parkingSlot, setParkingSlot] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [categoryId, setCategoryId] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [selectedParkingSlotIds, setSelectedParkingSlotIds] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [
    selectedDeleteParkingSlotCategory,
    setSelectedDeleteParkingSlotCategory,
  ] = useState(null);
  const [sortByCategory, setSortByCategory] = useState(() => {
    return sessionStorage.getItem("parkingSlots_sortByCategory") === "true";
  });

  const [searchValue] = useDebounce(searchInput, 400);

  useEffect(() => {
    fullReload();
  }, [searchValue]);

  useEffect(() => {
    sessionStorage.setItem("parkingSlots_sortByCategory", sortByCategory);
  }, [sortByCategory]);

  useEffect(() => {
    if (parkingSlots) {
      setParkingSlotsByCategory(sortByCategoryId(parkingSlots?.entries ?? []));
    }
  }, [parkingSlots]);

  if (!parkingSlots) {
    return <></>;
  }

  function sortByCategoryId(parking_slots) {
    return parking_slots.reduce((acc, item) => {
      if (!acc[item.parking_slot_category_id]) {
        acc[item.parking_slot_category_id] = { entries: [] };
      }
      acc[item.parking_slot_category_id].entries.push(item);
      return acc;
    }, {});
  }

  function reload() {
    return axios
      .get(`/api/v1/parking_slots.json`, {
        params: searchInput.length > 0 ? { search: searchInput } : {},
      })
      .then(function (response) {
        setParkingSlots(response.data);
      });
  }

  function fullReload() {
    reload();
    axios.get("/api/v1/parking_slot_categories.json").then(function (response) {
      setParkingSlotCategories(response.data);
    });
    axios
      .get("/api/v1/parking_slot_categories/template.json")
      .then(function (response) {
        setParkingSlotCategoryTemplate(response.data);
      });
  }

  function editCategory(category_id) {
    setCategoryId(category_id);
    toggleEditCategory(true);
  }

  function toggleActive(parkingSlot) {
    axios
      .put(`/api/v1/parking_slots/${parkingSlot.id}.json`, {
        parking_slot: {
          active: !parkingSlot.active,
        },
      })
      .then(function (response) {
        setParkingSlots((prevState) => {
          return {
            ...prevState,
            entries: prevState.entries.map((entry) => {
              if (entry.id === parkingSlot.id) {
                return response.data;
              } else {
                return entry;
              }
            }),
          };
        });
        showSuccessToast();
      })
      .catch(function (e) {
        showErrorToast(e.message);
      });
  }

  function toggleSelectedParkingSlot(id) {
    if (bulkSubmitting) return;
    setSelectedParkingSlotIds((ids) =>
      ids.includes(id)
        ? ids.filter((selectedId) => selectedId !== id)
        : [...ids, id]
    );
  }

  function submitBulkAction(action) {
    setBulkSubmitting(true);
    axios
      .patch("/api/v1/parking_slots/bulk_status.json", {
        parking_slot_ids: action.ids,
        active: action.active,
      })
      .then(() => {
        setSelectedParkingSlotIds([]);
        reload();
        showSuccessToast();
      })
      .catch((error) => {
        showValidationErrorToast(error);
      })
      .finally(() => setBulkSubmitting(false));
  }

  function submitStatusChange() {
    const action = pendingStatusChange;
    setPendingStatusChange(null);

    window.setTimeout(() => {
      if (action.parkingSlot) {
        toggleActive(action.parkingSlot);
      } else {
        submitBulkAction(action);
      }
    }, 0);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = parkingSlots.entries.findIndex(
        (s) => s.id === active.id
      );
      const newIndex = parkingSlots.entries.findIndex((s) => s.id === over.id);
      const newOrder = arrayMove(parkingSlots.entries, oldIndex, newIndex);

      setParkingSlots((prev) => ({ ...prev, entries: newOrder }));

      axios
        .put("/api/v1/parking_slots/reorder.json", {
          parking_slots: newOrder.map((s, index) => ({
            id: s.id,
            order: index,
          })),
        })
        .then(function () {
          showSuccessToast();
        })
        .catch(function (e) {
          showErrorToast(e.message);
          reload();
        });
    }
  }

  function handleEditSlotClose() {
    toggleEdit();
    setParkingSlot(null);
  }

  function handleEditSlotSuccess() {
    showSuccessToast();
    reload();
  }

  function handleEditCategoryClose() {
    toggleEditCategory();
    setCategoryId(null);
  }

  function handleEditCategorySuccess() {
    showSuccessToast();
    fullReload();
  }

  function deleteCategory() {
    axios
      .delete(
        `/api/v1/parking_slot_categories/${selectedDeleteParkingSlotCategory.id}.json`
      )
      .then(() => {
        showSuccessToast();
        fullReload();
      });
    setSelectedDeleteParkingSlotCategory(null);
  }

  function handleSearchChange(e) {
    setSearchInput(e.target.value);
    setSelectedParkingSlotIds([]);
    setPendingStatusChange(null);
  }

  function handleBulkActionChange(event) {
    if (event.target.value !== "") {
      setPendingStatusChange({
        ids: [...selectedParkingSlotIds],
        active: event.target.value === "activate",
      });
    }
  }

  function handleEditSlot(slot) {
    setParkingSlot(slot);
    toggleEdit();
  }

  const header = (
    <div className="d-flex w-100 align-items-center">
      <div className="col">
        <span className="font-weight-bold">
          Stellplätze (
          {
            parkingSlots?.entries?.filter((parking_slot) => parking_slot.active)
              .length
          }{" "}
          / {parkingSlots?.entries?.length})
        </span>
      </div>
      <div className="col d-flex justify-content-end">
        <Button variant={"primary"} onClick={toggleEdit} className="mr-2">
          <FontAwesomeIcon icon={faPlus} />
          <span className="pl-2">Stellplatz erstellen</span>
        </Button>
        <Button variant="secondary" onClick={toggleEditCategory}>
          <FontAwesomeIcon icon={faFolderPlus} />
          <span className="pl-2">Kategorie erstellen</span>
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <EditParkingSlotDialog
        show={showEdit}
        parking_slot={parkingSlot}
        parkingSlotCategories={parkingSlotCategories}
        toggle={handleEditSlotClose}
        handleSuccess={handleEditSlotSuccess}
      />
      <EditParkingCategoryDialog
        show={showEditCategory}
        category_id={categoryId}
        toggle={handleEditCategoryClose}
        handleSuccess={handleEditCategorySuccess}
      />

      {!!pendingStatusChange && (
        <ConfirmDialog
          handleSubmit={submitStatusChange}
          handleClose={() => setPendingStatusChange(null)}
          confirmBtnText={
            pendingStatusChange.parkingSlot
              ? "Speichern"
              : pendingStatusChange.active
              ? "Aktivieren"
              : "Deaktivieren"
          }
          content={
            pendingStatusChange.parkingSlot
              ? "Möchten Sie den Status des Parkplatzes sicher ändern?"
              : `Möchten Sie ${
                  pendingStatusChange.ids.length
                } Stellplätze wirklich ${
                  pendingStatusChange.active ? "aktivieren" : "deaktivieren"
                }?`
          }
        />
      )}
      {!!selectedDeleteParkingSlotCategory && (
        <ConfirmDialog
          handleSubmit={deleteCategory}
          handleClose={() => setSelectedDeleteParkingSlotCategory(null)}
          content={`Möchten Sie die Kategorie "${selectedDeleteParkingSlotCategory?.name}" wirklich löschen?`}
        />
      )}
      <Card title={header}>
        <Row>
          <Col md={3} xs={12}>
            <Form.Group className="mb-2">
              <div className="position-relative d-flex">
                <div className="position-absolute p-2">
                  <FontAwesomeIcon icon={faSearch} />
                </div>
                <Form.Control
                  placeholder="Stellplatz Nummer, Name ..."
                  className="input-icon-p ui-input-bg"
                  type="text"
                  value={searchInput}
                  disabled={bulkSubmitting}
                  onChange={handleSearchChange}
                />
              </div>
              <Form.Text className="text-muted">&nbsp;</Form.Text>
            </Form.Group>
          </Col>
          <Col
            xs={12}
            md
            className="d-flex align-items-start justify-content-end"
            style={{ paddingTop: "7px" }}
          >
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id="sortByCategory"
                checked={sortByCategory}
                onChange={(e) => setSortByCategory(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="sortByCategory">
                Nach Kategorie gruppieren
              </label>
            </div>
          </Col>
          <Col xs={12} className="d-flex flex-wrap align-items-center mb-3">
            <Checkbox
              name="select-all-parking-slots"
              label={`${selectedParkingSlotIds.length} ausgewählt`}
              aria-label="Alle Suchergebnisse auswählen"
              checked={
                parkingSlots.entries.length > 0 &&
                parkingSlots.entries.every((slot) =>
                  selectedParkingSlotIds.includes(slot.id)
                )
              }
              disabled={parkingSlots.entries.length === 0 || bulkSubmitting}
              onChange={(event) =>
                setSelectedParkingSlotIds(
                  event.target.checked
                    ? parkingSlots.entries.map((slot) => slot.id)
                    : []
                )
              }
              className="parking-slot-selection mr-3"
            />
            {selectedParkingSlotIds.length > 0 && (
              <SelectBox
                name="bulk_action"
                value={
                  pendingStatusChange && !pendingStatusChange.parkingSlot
                    ? pendingStatusChange.active
                      ? "activate"
                      : "deactivate"
                    : ""
                }
                options={[
                  { value: null, name: "Aktion auswählen" },
                  { value: "activate", name: "Aktivieren" },
                  { value: "deactivate", name: "Deaktivieren" },
                ]}
                disabled={bulkSubmitting || !!pendingStatusChange}
                onChange={handleBulkActionChange}
                className="w-auto"
              />
            )}
          </Col>
          <Col md={12} xs={12}>
            {sortByCategory ? (
              <Col xs={12} sm={12} className="d-flex w-100 flex-wrap">
                {parkingSlotCategories?.entries?.map((category) => (
                  <ParkingSlotCategory
                    key={category.id}
                    category={category}
                    callbackSetParkingSlot={handleEditSlot}
                    parking_slots={parkingSlotsByCategory[category.id]}
                    initialCollapsed={!category.is_default}
                    callbackSetSelectedTPS={(slot) =>
                      setPendingStatusChange({ parkingSlot: slot })
                    }
                    callbackEditCategory={editCategory}
                    callbackToggleDeleteCategory={
                      setSelectedDeleteParkingSlotCategory
                    }
                    selectedParkingSlotIds={selectedParkingSlotIds}
                    onToggleSelectedParkingSlot={toggleSelectedParkingSlot}
                    selectionDisabled={bulkSubmitting}
                  />
                ))}
              </Col>
            ) : (
              <div className="ui-table-container">
                <div className={`ui-list-table ${styles.table}`}>
                  <div className="ui-list-header">
                    <div className="ui-list-cell ui-drag-handle"></div>
                    <div className="ui-list-cell"></div>
                    <div className="ui-list-cell flex-1">Name</div>
                    <div className="ui-list-cell flex-1">Kategorie</div>
                    <div className="ui-list-cell flex-1"></div>
                  </div>
                  <div className="ui-list-body">
                    <DndContext
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={parkingSlots?.entries?.map((s) => s.id) ?? []}
                        strategy={verticalListSortingStrategy}
                      >
                        {parkingSlots?.entries?.map((slot) => {
                          const category = parkingSlotCategories?.entries?.find(
                            (c) => c.id === slot.parking_slot_category_id
                          );
                          return (
                            <SortableRow key={slot.id} id={slot.id}>
                              <div className="ui-list-cell">
                                <Checkbox
                                  name={`select-slot-${slot.id}`}
                                  aria-label={`Stellplatz ${slot.name} auswählen`}
                                  checked={selectedParkingSlotIds.includes(
                                    slot.id
                                  )}
                                  disabled={bulkSubmitting}
                                  onChange={() =>
                                    toggleSelectedParkingSlot(slot.id)
                                  }
                                  className="parking-slot-selection"
                                />
                              </div>
                              <div className="ui-list-cell flex-1">
                                <b>{slot.name}</b>
                              </div>
                              <div className="ui-list-cell flex-1">
                                <CategoryIcon category={category} size={28} />
                                <span className="ml-2">{category?.name}</span>
                              </div>
                              <div className="ui-list-cell flex-1">
                                <Row>
                                  <Col className="col-auto text-primary">
                                    <IconButton
                                      size="sm"
                                      textColor="#2769d8"
                                      className="bg-transparent border-0"
                                      icon={faPencil}
                                      onClick={() => handleEditSlot(slot)}
                                    />
                                  </Col>
                                  <Col className="col-auto">
                                    <div className="custom-control custom-switch">
                                      <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id={`slot-${slot.id}`}
                                        checked={slot.active}
                                        onChange={() =>
                                          setPendingStatusChange({
                                            parkingSlot: slot,
                                          })
                                        }
                                      />
                                      <label
                                        className="custom-control-label"
                                        htmlFor={`slot-${slot.id}`}
                                      >
                                        Aktiv
                                      </label>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </SortableRow>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                    {(parkingSlots?.entries?.length ?? 0) === 0 && (
                      <div className="ui-list-row">
                        <div className="ui-list-cell" style={{ flex: 1 }}>
                          Keine Stellplätze vorhanden
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ParkingSlotsPage;
